import { ProductFamilyCode } from "@bps/blocks-core";
import { NotFoundError } from "@bps/http-client";
import { useQuery } from "@tanstack/react-query";

import { usePermissionsChecker } from "../../auth/Permissions";
import { Permission } from "../../common-types/permission.enum";
import { useGateways } from "../../gateways-context";
import {
  PltCatalogComponentDto,
  PltCatalogPracticeLicenceDto,
  PltCatalogTenantFeaturesDto,
  TenantFeaturesCode,
  TenantFeaturesLastActionCode,
  TenantFeaturesLastActionStatus
} from "./plt-catalog.dtos";

enum PltCatalogCacheKeys {
  pltCatalogTenantFeatures = "plt-catalog-tenant-features",
  pltCatalogPracticeLicences = "plt-catalog-tenant-licences",
  pltCatalogComponent = "plt-catalog-component"
}
export const useTenantFeatures = (code: string) => {
  const { pltCatalogApi } = useGateways();

  return useQuery<PltCatalogTenantFeaturesDto | null>({
    queryKey: [PltCatalogCacheKeys.pltCatalogTenantFeatures, code],
    queryFn: async () => {
      try {
        return await pltCatalogApi.getTenantFeatures(code);
      } catch (error) {
        if (error instanceof NotFoundError) {
          return null;
        }
        return error;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity
  });
};

export const useTenantFeatureChecker = (
  prosBhbIntegration: TenantFeaturesCode
) => {
  const { data: tenantFeatures } = useTenantFeatures(prosBhbIntegration);

  if (!tenantFeatures) {
    return false;
  }

  const { lastActionCode, lastActionStatus, tenantFeatureStatusCode } =
    tenantFeatures;

  const isLastActionEnabled =
    lastActionCode === TenantFeaturesLastActionCode.enabled &&
    lastActionStatus === TenantFeaturesLastActionStatus.completed;

  const isFallbackStatusEnabled =
    tenantFeatureStatusCode === TenantFeaturesLastActionCode.enabled;

  return isLastActionEnabled || isFallbackStatusEnabled;
};

export const usePltCatalogPracticeLicences = () => {
  const { pltCatalogApi } = useGateways();
  const hasPermissions = usePermissionsChecker();

  return useQuery<PltCatalogPracticeLicenceDto[] | null>({
    queryKey: [PltCatalogCacheKeys.pltCatalogPracticeLicences],
    queryFn: () => pltCatalogApi.getPracticeLicences(),
    enabled: hasPermissions(Permission.PerPltLicenceRead)
  });
};

export const useHasPracticeLicence = (productFamilyCode: ProductFamilyCode) => {
  const { data } = usePltCatalogPracticeLicences();

  return !!data?.some(
    licence => licence.productFamilyCode === productFamilyCode
  );
};

export const usePltcCatalogComponent = (componentCode: string) => {
  const { pltCatalogApi } = useGateways();

  return useQuery<PltCatalogComponentDto | null>({
    queryKey: [PltCatalogCacheKeys.pltCatalogComponent, componentCode],
    queryFn: async () => {
      try {
        return await pltCatalogApi.getComponent(componentCode);
      } catch (error) {
        if (error instanceof NotFoundError) {
          return null;
        }
        return error;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity
  });
};
